import React, { useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { sendEmail, saveDraft } from '../api';

const TOOLBAR = [
  [{ font: [] }, { size: ['small', false, 'large'] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ color: [] }, { background: [] }],
  [{ align: [] }],
  ['link', 'image'],
  ['clean'],
];

export default function Compose({ mode = 'new', originalMessage, signature = '', onSend, onCancel }) {
  const isNew = mode === 'new';
  const isForward = mode === 'forward';

  const defaultTo = isNew || isForward
    ? []
    : [originalMessage?.from?.emailAddress?.address].filter(Boolean);

  const defaultSubject = isNew ? ''
    : isForward ? `FW: ${originalMessage?.subject || ''}`
    : `RE: ${originalMessage?.subject || ''}`;

  const sigBlock = signature && signature !== '<p><br></p>'
    ? `<br/><br/><hr style="border:none;border-top:1px solid #edebe9;margin:12px 0"/>${signature}`
    : '';

  const quoteBlock = !isNew
    ? `<br/><br/><hr style="border:none;border-top:1px solid #edebe9;margin:12px 0"/>
       <p style="color:#555;font-size:13px">
         <b>From:</b> ${originalMessage?.from?.emailAddress?.name || ''} &lt;${originalMessage?.from?.emailAddress?.address || ''}&gt;<br/>
         <b>Sent:</b> ${originalMessage?.receivedDateTime ? new Date(originalMessage.receivedDateTime).toLocaleString() : ''}<br/>
         <b>Subject:</b> ${originalMessage?.subject || ''}
       </p>${originalMessage?.body?.content || ''}`
    : '';

  const [to, setTo] = useState(defaultTo.join(', '));
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(sigBlock);
  const [importance, setImportance] = useState('normal');
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [error, setError] = useState('');
  const [showCc, setShowCc] = useState(false);

  async function handleSaveDraft() {
    setSaving(true); setError(''); setSavedMsg('');
    try {
      const toList = to.split(',').map(s => s.trim()).filter(Boolean);
      const ccList = cc.split(',').map(s => s.trim()).filter(Boolean);
      await saveDraft({ to: toList, cc: ccList, subject, body: body + quoteBlock, importance });
      setSavedMsg('Draft saved');
      setTimeout(() => setSavedMsg(''), 3000);
    } catch (e) {
      setError('Failed to save draft: ' + (e.response?.data?.error || e.message));
    } finally {
      setSaving(false);
    }
  }

  async function handleSend() {
    const toList = to.split(',').map(s => s.trim()).filter(Boolean);
    if (!toList.length) { setError('Please enter at least one recipient.'); return; }
    setSending(true); setError('');
    try {
      const fullBody = body + quoteBlock;
      if (onSend) {
        await onSend({ to: toList, cc: cc.split(',').map(s => s.trim()).filter(Boolean), subject, body: fullBody, importance });
      } else {
        await sendEmail({ to: toList, cc: cc.split(',').map(s => s.trim()).filter(Boolean), subject, body: fullBody, importance });
      }
      if (onCancel) onCancel();
    } catch (e) {
      setError('Failed to send: ' + (e.response?.data?.error || e.message));
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="compose-panel">
      <div className="compose-header">
        <span className="compose-title">{isNew ? 'New message' : isForward ? 'Forward' : 'Reply'}</span>
        <div className="compose-header-actions">
          <select value={importance} onChange={e => setImportance(e.target.value)} className="importance-select" title="Importance">
            <option value="low">↓ Low</option>
            <option value="normal">— Normal</option>
            <option value="high">! High</option>
          </select>
          {onCancel && <button className="compose-close" onClick={onCancel}>✕</button>}
        </div>
      </div>

      <div className="compose-fields">
        <div className="compose-field">
          <label>To</label>
          <input value={to} onChange={e => setTo(e.target.value)} placeholder="recipient@example.com" />
          <button className="cc-toggle" onClick={() => setShowCc(v => !v)}>Cc</button>
        </div>
        {showCc && (
          <div className="compose-field">
            <label>Cc</label>
            <input value={cc} onChange={e => setCc(e.target.value)} placeholder="cc@example.com" />
          </div>
        )}
        {(isNew || isForward) && (
          <div className="compose-field">
            <label>Subject</label>
            <input value={subject} onChange={e => setSubject(e.target.value)} />
          </div>
        )}
      </div>

      <div className="compose-quill-wrap">
        <ReactQuill
          theme="snow"
          value={body}
          onChange={setBody}
          modules={{ toolbar: TOOLBAR }}
          placeholder="Write your message…"
        />
      </div>

      {error && <div className="error-msg">{error}</div>}
      {savedMsg && <div className="draft-saved-msg">{savedMsg}</div>}

      <div className="compose-footer">
        <button className="btn-primary" onClick={handleSend} disabled={sending}>
          {sending ? 'Sending…' : '✉ Send'}
        </button>
        <button className="btn-secondary" onClick={handleSaveDraft} disabled={saving}>
          {saving ? 'Saving…' : '📋 Save Draft'}
        </button>
        {onCancel && <button className="btn-secondary" onClick={onCancel}>Discard</button>}
      </div>
    </div>
  );
}
